import AppError from '@/components/AppError';
import { TappAvatar } from '@/components/ui/TappAvatar';
import { TappIcon } from '@/components/ui/TappIcon';
import { TappInputFilter } from '@/components/ui/TappInputFilter';
import { TappLink } from '@/components/ui/TappLink';
import { TappPageHeader } from '@/components/ui/TappPageHeader';
import { TappPaginator } from '@/components/ui/TappPaginator';
import { TappPillProps, TappPill } from '@/components/ui/TappPill';
import { TappSelectFilter } from '@/components/ui/TappSelectFilter';
import { useApiTable, TappTableWrapper, TappTable } from '@/components/ui/TappTable';
import { cn } from '@/components/ui/utils';
import { UserStatus, Driver, getUserDisplayName } from '@/lib/models/user';
import { ApiUtils } from '@/lib/utils/api';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAlert, useConfirm, usePrompt } from '@/components/ui/alert-dialog-provider';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import Papa from 'papaparse';

const DriversPage = () => {
  const { t } = useTranslation();

  return (
    <>
      <TappPageHeader
        title={t('drivers')}
        backLink="/admin"
        actions={
          <TappLink
            to="/admin/drivers/new"
            className="inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
          >
            {t('addDriver')}
          </TappLink>
        }
      />
      <Table />
    </>
  );
};

const TableFilters = ({ onExportCsv }: { onExportCsv: () => void }) => {
  const { t } = useTranslation();

  return (
    <section className="mb-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <TappInputFilter
          name="search"
          border
          placeholder={t('search')}
          prefix={<TappIcon>search</TappIcon>}
          className="min-w-[240px]"
          clearable
        />

        <TappSelectFilter
          name="status"
          placeholder={t('driverStatus')}
          options={[
            { label: t('active'), value: UserStatus.active },
            { label: t('suspended'), value: UserStatus.suspended },
            { label: t('blocked'), value: UserStatus.blocked },
            { label: t('pending'), value: UserStatus.pending },
          ]}
          border
          className="min-w-[180px]"
        />
      </div>

      <Button variant="outline" onClick={onExportCsv}>
        <TappIcon className="mr-2">download</TappIcon>
        {t('exportCsv')}
      </Button>
    </section>
  );
};

const Table = () => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const alert = useAlert();
  const prompt = usePrompt();
  const queryClient = useQueryClient();

  const { mutateAsync: blockDriver, isPending: isBlocking } = useMutation({
    mutationFn: (driverId: string) => ApiUtils.put(`users/drivers/${driverId}/block`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drivers'] });
    },
  });

  const { mutateAsync: unblockDriver, isPending: isUnblocking } = useMutation({
    mutationFn: (driverId: string) => ApiUtils.put(`users/drivers/${driverId}/unblock`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drivers'] });
    },
  });

  const { mutateAsync: acceptDriver, isPending: isAccepting } = useMutation({
    mutationFn: (driverId: string) => ApiUtils.put(`users/drivers/${driverId}/accept`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drivers'] });
    },
  });

  const { mutateAsync: suspendDriver, isPending: isSuspending } = useMutation({
    mutationFn: ({ driverId, hours }: { driverId: string; hours: number }) =>
      ApiUtils.put(`users/drivers/${driverId}/suspend`, { hours }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drivers'] });
    },
  });

  const { mutateAsync: unsuspendDriver, isPending: isUnsuspending } = useMutation({
    mutationFn: (driverId: string) => ApiUtils.put(`users/drivers/${driverId}/unsuspend`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drivers'] });
    },
  });

  const handleBlockClick = async (driver: Driver) => {
    const ok = await confirm({
      title: t('blockDriver'),
      body: t('blockDriverConfirmBody', { name: getUserDisplayName(driver) }),
      cancelButton: t('cancel'),
      actionButton: t('confirmBlock'),
    });

    if (!ok) return;

    try {
      await blockDriver(driver._id);
    } catch (err: any) {
      if (err?.status === 409) {
        await alert({
          title: t('cannotBlockDriver'),
          body: t('cannotBlockDriverBody'),
        });
      } else {
        console.error(err);
      }
    }
  };

  const handleUnblockClick = async (driver: Driver) => {
    const ok = await confirm({
      title: t('unblockDriver'),
      body: t('unblockDriverConfirmBody', { name: getUserDisplayName(driver) }),
      cancelButton: t('cancel'),
      actionButton: t('confirmUnblock'),
    });

    if (!ok) return;

    try {
      await unblockDriver(driver._id);
    } catch (err: any) {
      if (err?.status === 409) {
        await alert({
          title: t('cannotUnblockDriver'),
          body: t('cannotUnblockDriverBody'),
        });
      } else {
        console.error(err);
      }
    }
  };

  const handleAcceptClick = async (driver: Driver) => {
    const ok = await confirm({
      title: t('acceptDriver'),
      body: t('acceptDriverConfirmBody', { name: getUserDisplayName(driver) }),
      cancelButton: t('cancel'),
      actionButton: t('confirmAccept'),
    });

    if (!ok) return;

    try {
      await acceptDriver(driver._id);
    } catch (err: any) {
      console.error(err);
      await alert({
        title: t('error'),
        body: ApiUtils.convertApiErrorToString(err),
      });
    }
  };

  const handleSuspendClick = async (driver: Driver) => {
    const hours = await prompt({
      title: t('suspendDriver'),
      body: t('suspendDriverPromptBody', { name: getUserDisplayName(driver) }),
      cancelButton: t('cancel'),
      actionButton: t('confirmSuspend'),
      defaultValue: '24',
      inputProps: {
        type: 'number',
        min: '1',
        placeholder: t('hoursPlaceholder'),
      },
    });

    if (!hours) return;

    const hoursNum = parseInt(hours, 10);
    if (isNaN(hoursNum) || hoursNum < 1) {
      await alert({
        title: t('error'),
        body: t('invalidHoursError'),
      });
      return;
    }

    try {
      await suspendDriver({ driverId: driver._id, hours: hoursNum });
    } catch (err: any) {
      console.error(err);
      await alert({
        title: t('error'),
        body: ApiUtils.convertApiErrorToString(err),
      });
    }
  };

  const handleUnsuspendClick = async (driver: Driver) => {
    const ok = await confirm({
      title: t('unsuspendDriver'),
      body: t('unsuspendDriverConfirmBody', { name: getUserDisplayName(driver) }),
      cancelButton: t('cancel'),
      actionButton: t('confirmUnsuspend'),
    });

    if (!ok) return;

    try {
      await unsuspendDriver(driver._id);
    } catch (err: any) {
      console.error(err);
      await alert({
        title: t('error'),
        body: ApiUtils.convertApiErrorToString(err),
      });
    }
  };

  const columns = useMemo(() => {
    const statusColors: Record<UserStatus, TappPillProps['color']> = {
      [UserStatus.active]: 'success',
      [UserStatus.suspended]: 'destructive',
      [UserStatus.blocked]: 'destructive',
      [UserStatus.pending]: 'amber',
      [UserStatus.deleted]: 'secondary',
    };
    const columnHelper = createColumnHelper<Driver>();
    return [
      columnHelper.accessor(
        (row) => ({
          src: row.profilePictureUrl,
          name: getUserDisplayName(row),
          phone: row.phoneNumber,
        }),
        {
          id: 'firstName',
          header: t('name'),
          enableSorting: true,
          cell: (info) => (
            <div className="flex items-center gap-4">
              <TappAvatar {...info.getValue()} />
              <p className="text-sm">{info.getValue().name}</p>
            </div>
          ),
        }
      ),
      columnHelper.accessor('phoneNumber', {
        header: t('phone'),
        enableSorting: true,
      }),
      columnHelper.accessor('carModel', {
        header: t('car'),
        enableSorting: true,
      }),
      columnHelper.accessor('licensePlate', {
        header: t('licensePlate'),
        enableSorting: true,
      }),
      columnHelper.accessor((row) => row.companyName || '-', {
        id: 'company',
        header: t('company'),
        enableSorting: false,
      }),
      columnHelper.accessor('rating', {
        header: t('rating'),
        enableSorting: true,
      }),
      columnHelper.accessor('status', {
        header: t('status'),
        enableSorting: true,
        cell: (info) => <TappPill color={statusColors[info.getValue()]}>{t(info.getValue())}</TappPill>,
      }),
      columnHelper.display({
        id: 'accept',
        header: '',
        cell: ({ row: { original } }) =>
          original.status === UserStatus.pending && (
            <button
              className="text-green-600 hover:underline"
              onClick={() => handleAcceptClick(original)}
              disabled={isAccepting}
            >
              {t('acceptDriver')}
            </button>
          ),
      }),
      columnHelper.display({
        id: 'suspend',
        header: '',
        cell: ({ row: { original } }) =>
          original.status === UserStatus.active ? (
            <button
              className="text-amber-600 hover:underline"
              onClick={() => handleSuspendClick(original)}
              disabled={isSuspending}
            >
              {t('suspend')}
            </button>
          ) : original.status === UserStatus.suspended ? (
            <button
              className="text-green-600 hover:underline"
              onClick={() => handleUnsuspendClick(original)}
              disabled={isUnsuspending}
            >
              {t('unsuspend')}
            </button>
          ) : null,
      }),
      columnHelper.display({
        id: 'block',
        header: '',
        cell: ({ row: { original } }) =>
          original.status === UserStatus.deleted ? null : original.status !== UserStatus.blocked &&
            original.status !== UserStatus.pending ? (
            <button
              className="text-red-600 hover:underline"
              onClick={() => handleBlockClick(original)}
              disabled={isBlocking}
            >
              {t('block')}
            </button>
          ) : original.status === UserStatus.blocked ? (
            <button
              className="text-green-600 hover:underline"
              onClick={() => handleUnblockClick(original)}
              disabled={isUnblocking}
            >
              {t('activate')}
            </button>
          ) : null,
      }),
      columnHelper.display({
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row: { original } }) =>
          original.status === UserStatus.deleted ? null : (
            <div className="flex items-center justify-end">
              <TappLink to={`/admin/drivers/${original._id!}`}>{t('edit')}</TappLink>
            </div>
          ),
      }),
    ];
  }, [t, isBlocking, isAccepting, isUnblocking, isSuspending, isUnsuspending]);

  const { table, isLoading, total, error } = useApiTable<Driver>({
    columns,
    queryKey: (filter) => ['drivers', filter],
    queryFn: (filter) =>
      ApiUtils.get('users/drivers', {
        query: { ...filter },
      }),
    enableSortingRemoval: true,
  });

  const handleExportCsv = () => {
    const rows = table.getRowModel().rows.map((row) => row.original);
    const csvData = rows.map((driver) => ({
      [t('name')]: getUserDisplayName(driver),
      [t('phone')]: driver.phoneNumber,
      [t('car')]: driver.carModel,
      [t('licensePlate')]: driver.licensePlate,
      [t('rating')]: driver.rating,
      [t('status')]: t(driver.status),
    }));

    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `drivers-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (error) return <AppError error={error} />;

  return (
    <>
      <TableFilters onExportCsv={handleExportCsv} />
      <TappTableWrapper className={cn({ isLoading })}>
        <TappTable table={table} stickyHeader />
        <TappPaginator table={table} total={total} />
      </TappTableWrapper>
    </>
  );
};

export default DriversPage;
